import { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
} from "@mui/material";
import { Box, Card, CardContent, Grid, Typography } from "../../../../components";
import { OrdemServicoService } from "../../../../services";
import { formatPrice, responseErros } from "../../../../utils";

const ordemServicoService = new OrdemServicoService();

const defaultData = {
  totais: { totalOS: 0, totalServicos: 0, totalProdutos: 0 },
  top5Servicos: [],
};

const COR_SERVICOS = "#388e3c";
const COR_PRODUTOS = "#1976d2";

function LegendaItem({ cor, label, valor, pct }) {
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
      <Box display="flex" alignItems="center" gap={1}>
        <Box
          sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: cor }}
        />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {label}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b" }}>
          {formatPrice(valor)}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.disabled", minWidth: 36, textAlign: "right" }}
        >
          {pct.toFixed(1)}%
        </Typography>
      </Box>
    </Box>
  );
}

function MonitorOsVendas({ filtrosBody }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(defaultData);

  useEffect(() => {
    buscarVendas();
  }, [filtrosBody]);

  async function buscarVendas() {
    setLoading(true);
    const result = await ordemServicoService.getRelatorioDashboardVendas(
      filtrosBody
    );
    if (!result.isAxiosError) {
      setData(result.data || defaultData);
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  const { totais, top5Servicos } = data;
  const totalOS = totais.totalOS || 0;
  const pctServicos = totalOS ? (totais.totalServicos / totalOS) * 100 : 0;
  const pctProdutos = totalOS ? (totais.totalProdutos / totalOS) * 100 : 0;

  const chartOptions = {
    chart: {
      type: "pie",
      height: 220,
      backgroundColor: "transparent",
      margin: [8, 8, 8, 8],
    },
    title: { text: "" },
    credits: { enabled: false },
    legend: { enabled: false },
    tooltip: {
      useHTML: true,
      backgroundColor: "#fff",
      borderWidth: 0,
      borderRadius: 4,
      pointFormatter: function () {
        return `<b>${this.name}</b>: ${formatPrice(
          this.y
        )} (${this.percentage.toFixed(1)}%)`;
      },
    },
    plotOptions: {
      pie: {
        innerSize: "72%",
        size: "90%",
        center: ["50%", "50%"],
        borderWidth: 2,
        borderColor: "#fff",
        dataLabels: { enabled: false },
        states: { hover: { halo: { size: 6 } } },
      },
    },
    series: [
      {
        name: "Distribuição",
        animation: false,
        data: [
          { name: "Serviços", y: totais.totalServicos, color: COR_SERVICOS },
          { name: "Peças / Produtos", y: totais.totalProdutos, color: COR_PRODUTOS },
        ],
      },
    ],
  };

  return (
    <Card sx={{ borderRadius: 3, border: "1px solid #f0f0f0", height: "100%" }}>
      <CardContent sx={{ p: 2 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Vendas de Serviços e Produtos
          </Typography>
          <i
            className="ph-bold ph-shopping-cart"
            style={{ color: "#64748b", fontSize: 20 }}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <Typography variant="body2" color="text.secondary">
              Carregando...
            </Typography>
          </Box>
        ) : totalOS === 0 ? (
          <Box display="flex" justifyContent="center" py={5}>
            <Typography variant="body2" color="text.secondary">
              Nenhuma O.S concluída no período selecionado.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box sx={{ position: "relative" }}>
                <HighchartsReact
                  highcharts={Highcharts}
                  options={chartOptions}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 800, fontSize: 17, color: "#1e293b", lineHeight: 1.1 }}
                  >
                    {formatPrice(totalOS)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Total
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 1, px: 1 }}>
                <LegendaItem
                  cor={COR_SERVICOS}
                  label="Serviços"
                  valor={totais.totalServicos}
                  pct={pctServicos}
                />
                <LegendaItem
                  cor={COR_PRODUTOS}
                  label="Peças / Produtos"
                  valor={totais.totalProdutos}
                  pct={pctProdutos}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={7}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Serviços Mais Contratados
              </Typography>
              {top5Servicos.length > 0 ? (
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Serviço</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Qtd
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Valor
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {top5Servicos.map((s, i) => (
                      <TableRow
                        key={i}
                        sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}
                      >
                        <TableCell
                          sx={{
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={s.name}
                        >
                          {s.name}
                        </TableCell>
                        <TableCell align="right">{Math.round(s.qty)}</TableCell>
                        <TableCell align="right">
                          {formatPrice(s.value)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  Sem serviços no período.
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}

export default MonitorOsVendas;
